import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { jobsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { 
    ArrowLeft, Edit, MapPin, Clock, Users, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function JobDetailPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadJob();
    }, [id]);

    const loadJob = async () => {
        try {
            const response = await jobsApi.getById(id);
            setJob(response.data);
        } catch (error) {
            toast.error(t('errors.generic_load'));
            navigate('/dashboard/jobs');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#D1FAE5] text-[#059669]">{t('jobs.badge_active')}</span>;
            case 'paused':
                return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#FEF3C7] text-[#D97706]">{t('jobs.badge_paused')}</span>;
            case 'closed':
                return <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#F3F4F6] text-[#6B7280]">{t('jobs.badge_closed')}</span>;
            default:
                return null;
        }
    };

    const getEmploymentLabel = (type) => {
        switch (type) {
            case 'full-time': return t('jobs.employment_full_time');
            case 'part-time': return t('jobs.employment_part_time');
            case 'contract': return t('jobs.employment_contract');
            case 'remote': return t('jobs.employment_remote');
            default: return type;
        }
    };

    const getLevelLabel = (level) => {
        switch (level) {
            case 'junior': return t('jobs.form_level_junior');
            case 'mid': return t('jobs.form_level_mid');
            case 'senior': return t('jobs.form_level_senior');
            case 'expert': return t('jobs.form_level_expert');
            default: return level;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    if (!job) return null;

    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';
    const formattedDate = job.created_at
        ? new Date(job.created_at).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
        : '-';

    const expMin = job.experience_min || 0;
    const expMax = job.experience_max || 0;

    return (
        <div className="max-w-3xl mx-auto" data-testid="job-detail-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <button 
                        onClick={() => navigate('/dashboard/jobs')}
                        className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors flex-shrink-0"
                        data-testid="back-to-jobs-btn"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#4B5563]" />
                    </button>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] font-['Outfit'] break-words">
                                {job.title}
                            </h1>
                            {getStatusBadge(job.status)}
                        </div>
                        <p className="text-[#4B5563] mt-1">{t('jobs.detail_subtitle')}</p>
                    </div>
                </div>
                {job.can_edit && (
                    <Button 
                        onClick={() => navigate(`/dashboard/jobs/${id}/edit`)}
                        className="bg-[#002FA7] hover:bg-[#002480] text-white w-full sm:w-auto"
                        data-testid="edit-job-btn"
                    >
                        <Edit className="w-4 h-4 mr-2" />
                        {t('common.edit')}
                    </Button>
                )}
            </div>

            <div className="space-y-6">
                {/* General info */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                        {t('jobs.detail_general')}
                    </h2>
                    
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-[#9CA3AF] mb-1">{t('jobs.detail_description_label')}</p>
                            <p className="text-[#0A0A0A] whitespace-pre-wrap">{job.description || '-'}</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#9CA3AF]" />
                                <div>
                                    <p className="text-xs text-[#9CA3AF]">{t('jobs.form_location_label')}</p>
                                    <p className="text-sm text-[#0A0A0A] font-medium">{job.location || t('jobs.detail_location_not_specified')}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-[#9CA3AF]" />
                                <div>
                                    <p className="text-xs text-[#9CA3AF]">{t('jobs.detail_employment_label')}</p>
                                    <p className="text-sm text-[#0A0A0A] font-medium">{getEmploymentLabel(job.employment_type)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-[#9CA3AF]" />
                                <div>
                                    <p className="text-xs text-[#9CA3AF]">{t('jobs.detail_candidates_label')}</p>
                                    <p className="text-sm text-[#0A0A0A] font-medium">{job.candidates_count || 0}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Experience */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                        {t('jobs.detail_experience_title')}
                    </h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-[#9CA3AF]">{t('jobs.detail_minimum')}</p>
                            <p className="text-[#0A0A0A] font-medium">{expMin <= 1 ? t('jobs.detail_years_one', { count: expMin }) : t('jobs.detail_years_other', { count: expMin })}</p>
                        </div>
                        <div>
                            <p className="text-sm text-[#9CA3AF]">{t('jobs.detail_maximum')}</p>
                            <p className="text-[#0A0A0A] font-medium">{expMax <= 1 ? t('jobs.detail_years_one', { count: expMax }) : t('jobs.detail_years_other', { count: expMax })}</p>
                        </div>
                    </div>
                </div>

                {/* Salary */}
                {(job.salary_min || job.salary_max) && (
                    <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                            {t('jobs.detail_salary')}
                        </h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-[#9CA3AF]">{t('jobs.detail_minimum')}</p>
                                <p className="text-[#0A0A0A] font-medium">
                                    {job.salary_min ? t('jobs.detail_salary_per_year', { amount: job.salary_min.toLocaleString(locale) }) : '-'}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-[#9CA3AF]">{t('jobs.detail_maximum')}</p>
                                <p className="text-[#0A0A0A] font-medium">
                                    {job.salary_max ? t('jobs.detail_salary_per_year', { amount: job.salary_max.toLocaleString(locale) }) : '-'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Skills */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB]">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-4">
                        {t('jobs.form_skills_title')}
                    </h2>
                    {job.skills_required?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {job.skills_required.map((skill, index) => (
                                <div 
                                    key={index}
                                    className="flex items-center gap-2 px-3 py-2 bg-[#F3F4F6] rounded-lg"
                                >
                                    <span className="text-[#0A0A0A]">{skill.name}</span>
                                    <span className="text-xs text-[#9CA3AF]">
                                        {getLevelLabel(skill.level)}
                                    </span>
                                    <span className="text-xs text-[#002FA7] font-medium">
                                        x{skill.weight}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-[#9CA3AF] text-sm">{t('jobs.detail_skills_empty')}</p>
                    )}
                </div>

                {/* Creation date */}
                <div className="text-sm text-[#9CA3AF] text-right">
                    {t('jobs.detail_created_on', { date: formattedDate })}
                </div>
            </div>
        </div>
    );
}
